/**
 * Display formatting for the Admin.
 *
 * Plain functions rather than components so the rules are testable and the
 * same date renders the same way in a table, a card and a detail page.
 */

/** `2026-09-24T00:00:00.000Z` -> `2026-09-24`. */
export function formatDate(instant: string): string {
  if (instant.length < 10) {
    return instant;
  }

  return instant.slice(0, 10);
}

/** `2026-09-24T12:34:56.000Z` -> `2026-09-24 12:34`. */
export function formatDateTime(instant: string): string {
  if (instant.length < 16) {
    return instant;
  }

  return `${instant.slice(0, 10)} ${instant.slice(11, 16)}`;
}

const UNITS: readonly { readonly limit: number; readonly divisor: number; readonly suffix: string }[] = [
  { limit: 60, divisor: 1, suffix: "s" },
  { limit: 3600, divisor: 60, suffix: "m" },
  { limit: 86_400, divisor: 3600, suffix: "h" },
  { limit: 2_592_000, divisor: 86_400, suffix: "d" },
];

/**
 * A short "how long ago" string.
 *
 * `now` is a parameter rather than a call to `Date.now()` so a test can pin it —
 * a relative-time function that reads the clock is untestable, and this one is
 * used in a table where an off-by-one is immediately visible.
 *
 * Anything older than a month is reported as a date, because "37d ago" is not
 * something a reader converts in their head.
 */
export function formatRelative(instant: string, now: number): string {
  const then = Date.parse(instant);

  if (Number.isNaN(then)) {
    return instant;
  }

  const seconds = Math.floor((now - then) / 1000);

  if (seconds < 0) {
    return "just now";
  }

  for (const unit of UNITS) {
    if (seconds < unit.limit) {
      const value = Math.max(1, Math.floor(seconds / unit.divisor));

      return `${String(value)}${unit.suffix} ago`;
    }
  }

  return formatDate(instant);
}

/** `1536` -> `1.5 KB`. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${String(bytes)} B`;
  }

  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value.toFixed(1)} ${units[unitIndex] ?? "KB"}`;
}

/**
 * A stable, readable key for a localization row.
 *
 * Used where a React key is needed and `entryId + locale` is not unique enough
 * on its own — a table that mixes entries needs both.
 */
export function localizationKey(entryId: string, locale: string): string {
  return `${entryId}:${locale}`;
}
